import React from 'react';
import { ArrowRight, Sparkles, ShieldCheck, Activity, BookOpenText, Stethoscope, Quote } from 'lucide-react';

const pillars = [
  {
    icon: ShieldCheck,
    title: 'We lead with prevention',
    desc: 'True healthcare starts before illness begins. We invest in early detection, targeted screening and proactive plans that keep problems from becoming diagnoses.',
  },
  {
    icon: Activity,
    title: 'We champion vitality & longevity',
    desc: 'Care built for how you want to live in ten and twenty years — energy, cognition, cardiometabolic health and functional strength.',
  },
  {
    icon: BookOpenText,
    title: 'We empower through health literacy',
    desc: 'We take the time to explain what your data means, why it matters, and how your choices change outcomes — so you can drive your own health.',
  },
  {
    icon: Stethoscope,
    title: 'We treat root causes, not just symptoms',
    desc: 'Functional, integrative and evidence-based — we go beyond suppressing symptoms to address the underlying drivers of disease.',
  },
];

const About = () => {
  return (
    <section id="about" className="py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        {/* Intro */}
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6 reveal">
            <div className="h-11 w-11 rounded-full grid place-items-center bg-[var(--brand-green)] text-[var(--brand-cream)] mb-5">
              <Sparkles size={18} strokeWidth={1.6} />
            </div>
            <div className="text-xs uppercase tracking-[0.24em] text-[var(--brand-gold)] mb-4">About Elevate</div>
            <h2 className="font-serif text-4xl lg:text-6xl text-green tracking-tight leading-[1.04]">
              Leading the future of <em className="italic">preventative</em> medicine.
            </h2>
            <p className="mt-6 text-[var(--brand-mute)] text-lg leading-relaxed max-w-xl">
              At Elevate Medical Clinics (previously Rensburg Medical Clinic), our philosophy is built around redefining healthcare through <span className="text-green">prevention</span>, <span className="text-green">vitality</span> and <span className="text-green">precision</span>. We believe true wellness begins long before illness arises.
            </p>
            <p className="mt-4 text-[var(--brand-mute)] text-lg leading-relaxed max-w-xl">
              We're a boutique practice in Mount Lawley, Perth. A multidisciplinary team of doctors, dietitians, nurses and support staff who work collaboratively on every plan — so you never have to piece your care together yourself.
            </p>
            <a href="#team" className="btn-primary mt-8 text-sm inline-flex">Meet the team <ArrowRight size={16} /></a>
          </div>

          <div className="lg:col-span-6 reveal">
            <div className="grid grid-cols-2 gap-4">
              {[
                ['30+', 'Years elevating health'],
                ['1,000+', 'Patients transformed'],
                ['6', 'Practitioners & staff'],
                ['30–60 min', 'Consult duration'],
              ].map(([v, l]) => (
                <div key={l} className="rounded-2xl p-6 border border-[rgba(31,58,46,0.1)] bg-[var(--brand-cream-2)]">
                  <div className="font-serif text-3xl lg:text-4xl text-green tracking-tight">{v}</div>
                  <div className="mt-2 text-xs uppercase tracking-[0.2em] text-[var(--brand-mute)]">{l}</div>
                </div>
              ))}
            </div>
            <div className="mt-4 rounded-2xl p-6 border border-[var(--brand-green)] bg-[var(--brand-green)] text-[var(--brand-cream)]">
              <div className="text-xs uppercase tracking-[0.22em] text-[var(--brand-gold-2)] mb-2">Our promise</div>
              <p className="text-[15px] leading-relaxed opacity-95">
                You will always be met with time, respect and a plan grounded in evidence. If something isn't working, we adjust — together.
              </p>
            </div>
          </div>
        </div>

        {/* Pillars */}
        <div className="mt-20 lg:mt-24">
          <div className="max-w-3xl reveal">
            <div className="text-xs uppercase tracking-[0.24em] text-[var(--brand-gold)] mb-4">Our clinical philosophy</div>
            <h3 className="font-serif text-3xl lg:text-5xl text-green tracking-tight leading-[1.05]">
              Four principles <em className="italic">guiding</em> every plan.
            </h3>
          </div>

          <div className="mt-12 grid md:grid-cols-2 gap-4">
            {pillars.map((p, i) => {
              const Ic = p.icon;
              return (
                <div key={p.title} className="reveal card-hover relative rounded-2xl p-7 border border-[rgba(31,58,46,0.08)] bg-[var(--brand-cream-2)]">
                  <div className="flex items-start gap-5">
                    <div className="h-11 w-11 rounded-full grid place-items-center bg-[var(--brand-green)] text-[var(--brand-cream)] shrink-0">
                      <Ic size={18} strokeWidth={1.6} />
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--brand-gold)] mb-1">Principle 0{i + 1}</div>
                      <div className="font-serif text-xl lg:text-2xl text-green leading-tight">{p.title}</div>
                      <p className="mt-3 text-[15px] text-[var(--brand-ink)] leading-relaxed opacity-90">{p.desc}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Founder quote */}
        <div className="mt-20 lg:mt-24 reveal">
          <div className="rounded-[28px] p-8 lg:p-14 bg-[var(--brand-green)] text-[var(--brand-cream)] relative overflow-hidden">
            <div className="absolute inset-0 grain pointer-events-none" />
            <div className="relative grid lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-8">
                <Quote size={32} className="text-[var(--brand-gold-2)]" />
                <p className="font-serif text-2xl lg:text-4xl leading-[1.2] mt-4">
                  “True healthcare starts before illness begins. <em className="italic">Prevention</em> is the new cure.”
                </p>
                <div className="mt-6 flex items-center gap-4">
                  <div className="h-12 w-12 rounded-full bg-[var(--brand-gold)] text-[var(--brand-green)] grid place-items-center font-serif text-lg">
                    AC
                  </div>
                  <div>
                    <div className="font-medium">Dr Anthony Chaffee</div>
                    <div className="text-xs uppercase tracking-[0.22em] text-[var(--brand-gold-2)] mt-1">Principal Doctor</div>
                  </div>
                </div>
              </div>
              <div className="lg:col-span-4">
                <div className="rounded-2xl p-6 bg-[rgba(247,243,236,0.06)] border border-[rgba(247,243,236,0.15)]">
                  <div className="text-xs uppercase tracking-[0.22em] text-[var(--brand-gold-2)] mb-3">A first for Australia</div>
                  <p className="text-[15px] opacity-95 leading-relaxed">
                    Australia's first specialised preventative & functional medicine clinic — combining decades of GP experience with modern longevity science.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
